import { useState, useCallback, useMemo } from "react";

/**
 * Unifies the standard list-toolbar controls used by filter option panels:
 * sort (asc/desc), showSelectedFirst, selectAll, clearAll, invertSelection.
 *
 * Search is intentionally left to the caller — its source (local vs. remote,
 * debounced vs. immediate) varies too much to fit cleanly here.
 *
 * Inputs:
 *   items      — raw available options
 *   value      — current selection [{ value, display, ... }]
 *   onChange   — (next) => void
 *   getKey     — (item) => string. Defaults to `item.value`.
 *   getDisplay — (item) => string. Defaults to `item.display`.
 *   mapEntry   — (item) => entry written into selection.
 *                Defaults to `{ value: getKey(item), display: getDisplay(item) }`.
 *                Override when entries need extra fields (e.g. parent context).
 *   isAvailable — (item) => boolean. Defaults to `!item.loading`.
 *
 * Returns:
 *   sortOrder, showSelectedFirst                     — current state
 *   orderedItems                                     — items after sort + showSelectedFirst
 *   selectAll, clearAll, invert                      — selection actions
 *   toggleSort, toggleShowSelectedFirst              — toolbar toggles
 */
export const useListControls = ({
  items = [],
  value = [],
  onChange,
  getKey = (o) => o.value,
  getDisplay = (o) => o.display,
  mapEntry,
  isAvailable = (o) => !o.loading,
}) => {
  const [sortOrder, setSortOrder] = useState(null); // null | "asc" | "desc"
  const [showSelectedFirst, setShowSelectedFirst] = useState(false);

  const resolvedMapEntry =
    mapEntry || ((o) => ({ value: getKey(o), display: getDisplay(o) }));

  const selectedSet = useMemo(
    () => new Set((Array.isArray(value) ? value : []).map((v) => v.value)),
    [value]
  );

  const orderedItems = useMemo(() => {
    let out = items;
    if (sortOrder) {
      out = [...out].sort((a, b) =>
        sortOrder === "asc"
          ? (getDisplay(a) || "").localeCompare(getDisplay(b) || "")
          : (getDisplay(b) || "").localeCompare(getDisplay(a) || "")
      );
    }
    if (showSelectedFirst) {
      out = [...out].sort(
        (a, b) =>
          (selectedSet.has(getKey(a)) ? 0 : 1) -
          (selectedSet.has(getKey(b)) ? 0 : 1)
      );
    }
    return out;
  }, [items, sortOrder, showSelectedFirst, selectedSet, getKey, getDisplay]);

  // `items` is the currently-visible (search-filtered) set. Select-all/Clear-all act on
  // it while preserving the selection state of anything outside the current search.
  const selectAll = useCallback(() => {
    const existing = new Set((Array.isArray(value) ? value : []).map((v) => v.value));
    const additions = items
      .filter((o) => isAvailable(o) && !existing.has(getKey(o)))
      .map(resolvedMapEntry);
    onChange([...(Array.isArray(value) ? value : []), ...additions]);
  }, [items, value, onChange, isAvailable, resolvedMapEntry, getKey]);

  const clearAll = useCallback(() => {
    const itemKeys = new Set(items.map(getKey));
    onChange((Array.isArray(value) ? value : []).filter((v) => !itemKeys.has(v.value)));
  }, [items, value, onChange, getKey]);
  
  const invert = useCallback(
    () =>
      onChange(
        items
          .filter((o) => isAvailable(o) && !selectedSet.has(getKey(o)))
          .map(resolvedMapEntry)
      ),
    [items, selectedSet, onChange, isAvailable, resolvedMapEntry, getKey]
  );
  const toggleSort = useCallback(
    () => setSortOrder((p) => (p === "asc" ? "desc" : "asc")),
    []
  );
  const toggleShowSelectedFirst = useCallback(
    () => setShowSelectedFirst((p) => !p),
    []
  );

  return {
    sortOrder,
    showSelectedFirst,
    orderedItems,
    selectAll,
    clearAll,
    invert,
    toggleSort,
    toggleShowSelectedFirst,
  };
};
